import React from "react";
import { Form } from "antd";
import { useTranslate } from "@refinedev/core";
import { Grid } from "@libertify/ui";
import { PlaybookAddOnCard } from "../../PlaybookAddOnCard";
import { PlaybookInput } from "../../PlaybookInput";
import { PlaybookSelect } from "../../PlaybookSelect";
import { PlaybookFormItem } from "../../PlaybookFormItem";
import { normalizeNumericInput, percentFieldProps } from "../../../helpers";
import { useAddonDraft } from "../../../context/PlaybookDraftContext";
import { QuizFormValues } from "../types";
import { QUIZ_DEFAULTS } from "../defaults";
import { useQuizConfigData } from "../hooks/useQuizConfigData";
import { quizDifficultyFieldProps } from "./helpers";
import { CONFIG_ITEM } from "../../../data/addons";

// TODO: replace with a real credit estimate once the backend exposes one for
// this add-on. Hardcoded for now (matches the catalog placeholder).
const ESTIMATED_CREDITS = 80;

export const QuizConfigCard: React.FC = () => {
  const t = useTranslate();
  const [form] = Form.useForm<QuizFormValues>();
  const { difficultyOptions } = useQuizConfigData();
  const { initialValues, onValuesChange } = useAddonDraft(
    CONFIG_ITEM.Quiz,
    QUIZ_DEFAULTS
  );

  return (
    <PlaybookAddOnCard
      itemId={CONFIG_ITEM.Quiz}
      title={t("createExperience.addOns.quiz.title")}
      credits={ESTIMATED_CREDITS}
      description={t("createExperience.addOns.quiz.description")}
    >
      <Form<QuizFormValues>
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={onValuesChange}
      >
        <Grid columns={2} gap="lg">
          <PlaybookFormItem
            name={["generation_settings", "difficulty"]}
            labelKey="createExperience.addOns.quiz.difficulty.label"
            {...quizDifficultyFieldProps}
          >
            <PlaybookSelect options={difficultyOptions} />
          </PlaybookFormItem>

          <PlaybookFormItem
            name={["functional_settings", "nb_of_questions_per_subset"]}
            labelKey="createExperience.addOns.quiz.questionsPerAttempt"
            normalize={normalizeNumericInput}
          >
            <PlaybookInput type="number" min={1} max={25} />
          </PlaybookFormItem>

          <PlaybookFormItem
            name={["functional_settings", "passing_score"]}
            labelKey="createExperience.addOns.quiz.passingScore"
            // Stored as a fraction (API unit); displayed as a 0-100 percent.
            {...percentFieldProps}
          >
            <PlaybookInput type="number" min={0} max={100} />
          </PlaybookFormItem>

          {/* Hidden while grading is out of scope — re-enable (together with
              their QUIZ_DEFAULTS entries) if grading is going to be taken into
              account for this add-on. */}
          {/* <PlaybookFormItem
            name="maxAttemptsPerViewer"
            labelKey="createExperience.addOns.quiz.maxAttemptsPerViewer"
          >
            <PlaybookInput type="number" min={1} />
          </PlaybookFormItem>
          <PlaybookFormItem
            name="timesEachQuestionIsShown"
            labelKey="createExperience.addOns.quiz.timesEachQuestionIsShown"
          >
            <PlaybookInput type="number" min={1} />
          </PlaybookFormItem> */}

          {/* `question_order` is gone from QuizFunctionalSettings — the API
              dropped it for Quiz (it still exists on Trivia), so the
              randomize toggle has no field to bind to and is not coming back
              in this form. */}

          {/* Hidden for now — re-enable together with its QUIZ_DEFAULTS entry
              and a `Toggle` import from @libertify/ui. */}
          {/* <PlaybookFormItem
            name="showImprovementTipsAtEnd"
            labelKey="createExperience.addOns.quiz.showImprovementTipsAtEnd"
            valuePropName="checked"
          >
            <Toggle size="l" />
          </PlaybookFormItem> */}
        </Grid>
      </Form>
    </PlaybookAddOnCard>
  );
};

export default QuizConfigCard;
